import { useCallback, useState } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// components
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import { fDateTime } from 'src/utils/format-time';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Chip, MenuItem } from '@mui/material';
import { stringAvatar } from 'src/utils/avatar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { paths } from '../../routes/paths';
import { useRouter } from '../../routes/hook';
import { EventDetailsDto, InvoiceStatuses, TicketDetailsDto, TicketsService } from '../../api';
import { useLocales } from '../../locales';

// ----------------------------------------------------------------------

type Props = {
  ev: EventDetailsDto;
  tickets: TicketDetailsDto[];
  onDelete: VoidFunction;
};

export default function EventDetailsTickets({ ev, tickets, onDelete }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const router = useRouter();

  const { t } = useLocales();

  const handleDelete = useCallback(
    (item: number) => {
      const request = async () => {
        await TicketsService.cancelTicket({
          ticketId: item,
        });

        enqueueSnackbar(t('Ticket cancelled!'));

        onDelete();
      };
      request();
    },
    [onDelete, enqueueSnackbar, t]
  );

  const popover = usePopover();

  const [selectedSignUp, setSelectedSignUp] = useState<TicketDetailsDto | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, row: TicketDetailsDto) => {
    setSelectedSignUp(row);
    popover.onOpen(event);
  };

  const extraFields =
    tickets.length > 0
      ? (ev.options ?? []).map(
          (e) =>
            ({
              field: e.name!,
              headerName: t(e.name!),
              flex: 1,
              display: 'flex',
            } as any)
        )
      : [];

  const rows = tickets.map((x) => {
    const sorted = [...(x.options ?? [])].sort((a, b) =>
      a.name && b.name ? a.name.localeCompare(b.name) : 0
    );

    const dict = {} as any;
    sorted.forEach((el, index) => {
      dict[el.name!] = el.value!;
    });

    return { ...x, ...dict };
  });

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'participantName',
      headerName: t('Participant'),
      flex: 1,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Stack
          direction="row"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => router.push(paths.members.view(params.row.participantId!))}
        >
          {params.row?.participantProfileImageUrl === undefined && (
            <Avatar
              {...stringAvatar(`${params.row?.participantName}`, {
                mr: 2,
                mt: 1,
                mb: 1,
                width: 48,
                height: 48,
              })}
            />
          )}
          {params.row?.participantProfileImageUrl !== undefined && (
            <Avatar
              alt={`${params.row?.participantName}`}
              src={params.row?.participantProfileImageUrl}
              sx={{ mr: 2, mt: 1, mb: 1 }}
            />
          )}

          <ListItemText
            primary={`${params.row?.participantName}`}
            secondary={params.row?.participantEmail}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
          />
        </Stack>
      ),
    },
    {
      field: 'ticketTypeName',
      headerName: t('Ticket type'),
      flex: 1,
      display: 'flex',
    },
    ...extraFields,
    {
      field: 'createdAt',
      headerName: t('Purchased on'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => fDateTime(value),
    },
    {
      field: 'paymentStatus',
      headerName: t('Status'),
      flex: 1,
      display: 'flex',
      valueGetter: (value) => t(value),
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Chip
          color={
            (params.row.paymentStatus === InvoiceStatuses.Paid && 'success') ||
            (params.row.paymentStatus !== InvoiceStatuses.Paid && 'error') ||
            'default'
          }
          label={t(`${params.value}`)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      renderHeader: () => '',
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <IconButton
          color={popover.open ? 'inherit' : 'default'}
          onClick={(e) => handleOpenPopover(e, params.row)}
        >
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowHeight={() => 'auto'}
        initialState={{
          sorting: {
            sortModel: [{ field: 'status', sort: 'asc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        checkboxSelection={false}
        disableRowSelectionOnClick
        showToolbar
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="top-right">
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            handleDelete(selectedSignUp!.id!);
          }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t('Remove')}
        </MenuItem>
      </CustomPopover>
    </>
  );
}
